/**
 * Professional-portal domain: the signed-in firm member's workspace (real client list + firm
 * identity + billing gates) and the professional "case" lifecycle (in-person/invite intake,
 * opening a case's journey, SOA draft/publish/delete). Extracted out of App.tsx, which composes
 * this alongside the consumer-side session/auth and page-routing concerns.
 */
import { useState, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import { rearmAdviserIntro } from '../../components/narrative/index.tsx';
import { freshCoupleValidation } from '../../constants/coupleValidationDefaults.ts';
import type { CoupleValidationState } from '../agedCare/AgedCareAnalyser.tsx';
import { getSessionUser } from '../../services/authClient.ts';
import type { SessionUser } from '../../services/authClient.ts';
import { getPartnerMembership, getTenant, createClientInvite, resendClientInvite, updateFirmSettings, listFirmMembers, holderLabelFor, type Profession } from '../../services/tenantApi.ts';
import { adviceAttribution, setCurrentAdviceAttribution } from './adviceAttribution.ts';

/**
 * Our own licence, for advice given under it rather than a firm's (an accountant's clients, say).
 *
 * The AFSL number is deliberately absent until it is real. adviceAttribution() reports the line as
 * incomplete without it rather than printing a placeholder, because a made-up licence number on a
 * client document looks finished and so nobody goes looking for it.
 */
const PLATFORM_LICENCE = { entityName: 'AgedCareWise Pty Ltd', afslNumber: null };
import type { FirmSettings, MessageDefaults } from '../../services/tenantApi.ts';
import { setPersistentFirmAssumptions } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { FirmCapitalMarketAssumptions } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { Membership } from '../../services/tenantApi.ts';
import { getPartnerSummary } from '../../services/billingApi.ts';
import type { PartnerBillingSummary } from '../../services/billingApi.ts';
import {
  listClientProfiles,
  loadClientProfile,
  saveClientProfile,
  clearSessionMirror,
  restoreSessionMirror,
} from '../../services/profileApi.ts';
import type { ClientProfileSummary, ClientProfileWorkflow, ProfileAppData, ProfileAppState } from '../../services/profileApi.ts';
import { publishRegisteredSoa, listSoas } from '../../services/adviceApi.ts';
import type { RegisteredSoaSummary } from '../../services/adviceApi.ts';
import { getRoleScope } from './demoData.tsx';
import { derivePipelineStage } from './pipelineStage.ts';
import type { DemoCase } from './demoData.tsx';
import type { PartnerRole } from './PartnerPortal.tsx';
import { INITIAL_FACT_FIND } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy, SavedCaseStudy } from '../../pages/Dashboard/dashboardConfig.tsx';
import { notify } from '../../components/ui/notices.tsx';
import { inviteFileSeed } from './inviteCompleter.ts';

/** Screens a signed-in client can be returned to when they resume. */
export const RESUMABLE_PAGES = ['Dashboard', 'Client Profile', 'AssumptionsPage', 'AgedCarePage', 'HomeCarePage', 'RetirementVillagePage', 'GrannyFlatPage', 'DownsizingPage'];

/** REAL portal mode identity — Membership (tenantApi.ts) plus the display identity assembled from
 *  the session user + tenant record (see the workspace-resolution effect below). */
export interface PartnerFirmState extends Membership {
  identity: {
    adviserName: string;
    email: string;
    firmName: string;
    initials: string;
    roleLine: string;
    licenceLine: string;
    /**
     * The three facts a screen needs to DO something rather than only describe: which firm, who I am
     * in it, and whether I am the principal. Carried on the identity because that is what the portal
     * is handed — the surrounding Membership never reaches it.
     */
    tenantId: string;
    userId: string;
    role: Membership['role'];
    /**
     * What kind of firm. Decides whether the licensing questions exist at all (only an adviser firm
     * gives its own advice) and what the people who hold clients are called.
     */
    profession: Profession;
    /** "Adviser", "Accountant", "Solicitor" or "Client lead" — see holderLabelFor. */
    holderLabel: string;
    /**
     * Whether the firm has said HOW its advice is provided. The one setup step whose absence has a
     * consequence a firm would care about: until it is answered, an SOA cannot name the right
     * entities. Computed from the tenant rather than inferred from a licence line, so the dashboard
     * is never guessing from a string.
     */
    firmProfileComplete: boolean;
  };
}

/** Patch applied to a professional-portal case's engagement display labels. */
interface EngagementLabelsPatch {
  clientName?: string;
  clientEmail?: string;
}

/**
 * This hook's own professional-portal case shape: demoData.tsx's DemoCase (the analytics-rich demo
 * sample shape) plus the handful of fields only ever populated for REAL cases or particular intake
 * flows (isReal, currentSoaId, lastWorkedPage, invite/in-person completer metadata). PartnerPortal's
 * PartnerCase (a DemoCase superset with its own optional extras) and DemoCase itself are both
 * structurally satisfied by this — the extra fields here are all optional.
 */
export interface AppProfessionalCase extends DemoCase {
  isReal?: boolean;
  currentSoaId?: string;
  lastWorkedPage?: string;
  /** REAL cases only — the server-derived workflow facts behind the pipeline stage (pipelineStage.ts).
   *  `profileProgressIsEstimated` marks a percentage inferred from the stage rather than measured
   *  from the fact-find, so the UI can show it as an approximation instead of asserting a figure. */
  profileProgressIsEstimated?: boolean;
  profileCompletedSteps?: number | null;
  profileTotalSteps?: number | null;
  savedModelCount?: number;
  soaCount?: number;
  /** The raw server-derived workflow block — what the Activity Feed reads its timestamps from. */
  workflow?: ClientProfileWorkflow;
  clientMobile?: string;
  /** REAL cases only — the member responsible for this client, and who may see the file. */
  adviserUserId?: string | null;
  visibility?: 'firm' | 'restricted';
  inviteLink?: string | null;
  inPersonCompletingAs?: string;
  inviteCompletingAs?: string;
  inviteAutoFollowUp?: string;
}

/** "Add in person" form payload (PartnerPortal's InPersonCaseDetails is the same shape with every
 *  field required — optional here so the caller can invoke this with `{}` / partial data). */
export interface InPersonCasePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  preferredPhone?: string;
  completingAs?: string;
}

/** "Send invite" form payload (PartnerPortal's InviteCaseDetails is the same shape with every field
 *  required — optional here so the caller can invoke this with `{}` / partial data). */
export interface InviteCasePayload {
  firstName?: string;
  lastName?: string;
  /** When someone other than the client completes the form: their relationship to the client. */
  relationship?: string;
  email?: string;
  mobile?: string;
  accessMode?: string;
  autoFollowUp?: string;
  completingAs?: string;
}

/** When primary applicant details exist in the fact-find, use them for Partner Portal list (overrides placeholders like "Applicant TBC (completer)"). */
function deriveEngagementLabelsFromFactFind(fd: DraftFactFind | null | undefined): EngagementLabelsPatch {
  if (!fd) return {};
  const patch: EngagementLabelsPatch = {};
  const cf = (fd.client?.firstName || '').trim();
  const cl = (fd.client?.lastName || '').trim();
  const pf = (fd.partner?.firstName || '').trim();
  const pl = (fd.partner?.lastName || '').trim();
  const email = (fd.client?.email || '').trim();
  const adviceFor = fd.adviceFor || 'single';

  if (adviceFor === 'couple' && cf && pf) {
    const last = (cl || pl || '').trim();
    patch.clientName = last ? `${cf} & ${pf} ${last}` : `${cf} & ${pf}`;
  } else if (cf || cl) {
    patch.clientName = [cf, cl].filter(Boolean).join(' ').trim();
  }

  if (email) {
    patch.clientEmail = email;
  }
  return patch;
}

/**
 * Resolve the signed-in user's partner-firm membership, robustly.
 *
 * CRITICAL distinction: a returning partner must NEVER be dropped into the "Become a partner"
 * signup wizard because of a transient hiccup (a cold-server timeout right after their MFA code).
 * So this returns { ok, membership }:
 *   - ok:true,  membership:<obj>  → they belong to a firm
 *   - ok:true,  membership:null   → we asked successfully and they genuinely have NO firm
 *   - ok:false, membership:null   → we COULDN'T determine it (network/timeout). Callers must treat
 *                                   this as "unknown", never as "no firm".
 * Retries a few times because the first request to a sleeping free-tier server can time out while
 * it wakes; a retry then succeeds.
 */
export async function resolveFirmMembershipStatus(attempts = 3): Promise<{ ok: boolean; membership: Membership | null }> {
  for (let i = 0; i < attempts; i += 1) {
    try {
      return { ok: true, membership: await getPartnerMembership() };
    } catch {
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 1200));
    }
  }
  return { ok: false, membership: null };
}

export interface UseProfessionalPortalParams {
  accountUser: SessionUser | null;
  activePage: string;
  setActivePage: Dispatch<SetStateAction<string>>;
  factFind: DraftFactFind;
  setFactFind: Dispatch<SetStateAction<DraftFactFind>>;
  clientProfileStep: number;
  setClientProfileStep: Dispatch<SetStateAction<number>>;
  setSavedStrategies: Dispatch<SetStateAction<SavedStrategy[]>>;
  /** The Strategy Analysis Setup answers: per FILE, so they are put back whenever the file changes (owner, 18 Oct 2026). */
  setCoupleValidation: Dispatch<SetStateAction<CoupleValidationState>>;
  savedCaseStudies: SavedCaseStudy[];
  setSavedCaseStudies: Dispatch<SetStateAction<SavedCaseStudy[]>>;
  /** Opens the admin console instead of onboarding when the server confirms a platform admin.
   *  Resolves true when it did. See App.tsx. */
  routePlatformAdminWithoutFirm: () => Promise<boolean>;
}

export function useProfessionalPortal({
  accountUser,
  activePage,
  setActivePage,
  factFind,
  setFactFind,
  clientProfileStep,
  setClientProfileStep,
  setSavedStrategies,
  setCoupleValidation,
  savedCaseStudies,
  setSavedCaseStudies,
  routePlatformAdminWithoutFirm,
}: UseProfessionalPortalParams) {
  const [professionalRole, setProfessionalRole] = useState<PartnerRole>('relationship_owner');
  /*
   * EMPTY, not a set of samples. This used to be seeded with twenty-four fabricated client files, so
   * the portal opened holding other people's names, ages and care situations until the real list
   * arrived — and if that fetch was slow or failed, an adviser sat looking at a book of business that
   * did not exist. Demo data has no business being the starting state of a screen that shows real
   * clients (owner's decision to remove the demo portal, 11 Sep 2026).
   */
  const [professionalCases, setProfessionalCases] = useState<AppProfessionalCase[]>([]);
  const [activeProfessionalCaseId, setActiveProfessionalCaseId] = useState<string | null>(null);
  /** Increments when Partner Portal "Review" is clicked so the portal can switch to Reports & SOAs even if already on PartnerPortal. */
  const [partnerPortalReviewNonce, setPartnerPortalReviewNonce] = useState(0);

  // True while a sign-in was started from the partner door (routes to the portal, not resume).
  const [partnerIntent, setPartnerIntent] = useState(false);
  // REAL portal mode: the signed-in user's firm membership, and (while an adviser has a real
  // client file open) that file's id — auto-save then targets the client's record.
  const [partnerFirm, setPartnerFirm] = useState<PartnerFirmState | null>(null);
  const [activeRealProfileId, setActiveRealProfileId] = useState<string | null>(null);
  // True while the portal is resolving a signed-in firm member's workspace — the portal shows
  // a branded loading state instead of ever flashing the demo login (owner-reported race).
  const [partnerResolving, setPartnerResolving] = useState(false);
  // Workspace resolution failed (server waking / network blip): show retry, never a dead screen.
  const [portalEntryError, setPortalEntryError] = useState(false);
  const [portalRefreshNonce, setPortalRefreshNonce] = useState(0);

  // Partner subscription gate — shown when a firm member enters the portal but the FIRM has no
  // active entitlement (and billing is live). Same safety: off while billing is off.
  const [partnerPaywall, setPartnerPaywall] = useState<{ firmName: string; tenantId: string } | null>(null);
  // Partner client-billing summary (plan/capacity/remaining) for the active firm, + the "add
  // another client" paywall when they run out of paid capacity mid-portal.
  const [partnerBilling, setPartnerBilling] = useState<PartnerBillingSummary | null>(null);
  const [addClientPaywall, setAddClientPaywall] = useState<{ summary: PartnerBillingSummary | null; tenantId: string } | null>(null);
  const [showPartnerWizard, setShowPartnerWizard] = useState(false);
  /** Advice-register versions per REAL profile id — the server-side truth behind Reports & SOAs. */
  const [registeredSoasByProfileId, setRegisteredSoasByProfileId] = useState<Record<string, RegisteredSoaSummary[]>>({});
  /** The firm's working preferences (tenants.settings) — e.g. the default follow-up interval. */
  const [firmSettings, setFirmSettings] = useState<FirmSettings>({});
  /**
   * What this SERVER does about unresponsive clients, as opposed to what the firm has chosen. Kept
   * apart from firmSettings on purpose: one is the adviser's preference, the other is whether the
   * machinery honouring it is switched on, and conflating them is how a screen ends up describing a
   * setting nobody is acting on.
   */
  const [autoReminders, setAutoReminders] = useState<{ enabled: boolean; maxPerClient: number } | null>(null);
  /** Our standard wording, from the server — shown so an adviser can read what we'd send. */
  const [messageDefaults, setMessageDefaults] = useState<MessageDefaults | null>(null);
  /** Whether the Analytics tab's underlying fact-finds have been fetched yet (see loadFactFindsForAnalytics). */
  const [analyticsDataState, setAnalyticsDataState] = useState<'idle' | 'loading' | 'loaded' | 'partial'>('idle');

  /** Real client files, dressed in the case shape the portal UI already renders. Strategy-engine
   *  fields (strategySource, homeDecision, roomFunding, etc.) don't exist yet for a freshly-listed
   *  real file — they're demo-only analytics dimensions — so sensible neutral defaults are used;
   *  factFind is a placeholder until loaded (encrypted server-side; fetched per file on open).
   *
   *  The STAGE, though, is real: derived from the server's workflow metadata (fact-find progress,
   *  saved models, SOAs) by derivePipelineStage. This used to be hardcoded to "started / 50% / no
   *  SOA", which is why a fully-modelled client with a generated SOA still sat in In Progress. */
  /**
   * A firm's client file always arrives WITH its id: ids are withheld only from a caller whose one
   * visible file is their own, and a firm member is staff of the tenant these live in. See
   * resolveSoleProfileId — the id is required here rather than optional so that stops being a guess.
   */
  const mapProfileToCase = (
    p: Pick<ClientProfileSummary, 'displayName' | 'createdAt' | 'updatedAt' | 'createdBy' | 'adviserUserId' | 'visibility' | 'workflow'> & {
      id: string;
    },
  ): AppProfessionalCase => {
    const stage = derivePipelineStage(p.workflow);
    return {
    id: p.id,
    isReal: true,
    clientName: p.displayName || 'Client',
    clientEmail: '',
    // The adviser RESPONSIBLE for the file — which is not the same as who created it. Support can
    // prepare a file for an adviser, and it belongs on that adviser's book from the moment it exists.
    // Falls back to the creator for files that pre-date the column. scopedProfessionalCases matches
    // this against the signed-in user; the server enforces the same rule independently.
    ownerId: p.adviserUserId || p.createdBy || 'firm',
    adviserUserId: p.adviserUserId ?? null,
    visibility: p.visibility ?? 'firm',
    ownerName: '',
    ownerRole: 'adviser',
    practiceId: 'firm',
    accessMode: 'in_person',
    status: stage.status,
    profileProgressPct: stage.progressPct,
    profileProgressIsEstimated: stage.progressIsEstimated,
    profileCompletedSteps: p.workflow?.completedSteps ?? null,
    profileTotalSteps: p.workflow?.totalSteps ?? null,
    savedModelCount: p.workflow?.modelCount ?? 0,
    soaCount: p.workflow?.soaCount ?? 0,
    workflow: p.workflow,
    startedAt: p.createdAt,
    lastActivityAt: p.updatedAt,
    submissionDate: stage.submissionDate,
    soaStatus: stage.soaStatus,
    agedCareRoomCost: 0,
    sahBudget: 0,
    commonStrategy: 'Not set',
    estateStage: 'unknown',
    strategySource: 'manual_control',
    strategyGenerationCount: 0,
    strategyUsageMode: 'manual_only',
    manualStrategiesModelled: 0,
    manualStrategiesSaved: 0,
    suggestionSoaSelection: null,
    homeDecision: 'keep_home',
    assetSellMix: [],
    roomFunding: { mode: 'partial_rad', radPct: 0, dapPct: 0, radAmount: 0, dapAmount: 0 },
    // Real modelling numbers land here once the file is opened and its saved models load; a list row
    // only ever carries the counts above.
    factFind: structuredClone(INITIAL_FACT_FIND),
    };
  };

  // Entering the portal signed-in as a firm member switches it to REAL mode: the firm's
  // actual client list replaces the demo cases. No membership → demo stays untouched.
  useEffect(() => {
    if (activePage !== 'PartnerPortal' || !accountUser) return;
    setPartnerResolving(true);
    setPortalEntryError(false);
    setPartnerPaywall(null);
    setProfessionalCases([]); // drop the demo samples immediately — real mode never shows them
    (async () => {
      // A transient failure (server waking from sleep, network blip) must NOT be read as
      // "you have no firm" — that showed real members the onboarding wizard and could strand
      // them on the loader. Retry; on continued failure show a retry screen; only a
      // POSITIVELY-confirmed "no membership" opens the wizard.
      const res = await resolveFirmMembershipStatus();
      if (!res.ok) {
        setPartnerResolving(false);
        setPortalEntryError(true);
        return;
      }
      const membership = res.membership;
      if (!membership) {
        // Confirmed: signed in but not a firm member. A platform admin goes to the admin console;
        // anyone else is offered onboarding.
        const routed = await routePlatformAdminWithoutFirm();
        setPartnerResolving(false);
        if (!routed) setShowPartnerWizard(true);
        return;
      }
      // Firm billing gate: if billing is live and the FIRM hasn't chosen a plan yet
      // (summary.mode === 'none'), show the setup chooser instead of the portal. A firm on the
      // annual plan, pay-as-you-go, or free access is "set up" and goes straight in. The summary
      // is kept so the portal can gate "add client" and show the billing panel. (Off while
      // billing is off — summary is null and nothing gates.)
      const billing = await getPartnerSummary(membership.tenantId);
      if (billing.billingConfigured && billing.summary?.mode === 'none') {
        setPartnerResolving(false);
        setPartnerPaywall({ firmName: membership.tenantName, tenantId: membership.tenantId });
        return;
      }
      setPartnerBilling(billing.summary);
      // Assemble the REAL identity the portal displays (adviser from the login, firm +
      // licensing from the tenant record) — no more demo stage names.
      const tenant = await getTenant(membership.tenantId).catch(() => null);
      // The firm's own working preferences — currently just how soon to chase a client who hasn't
      // responded. Read once here so the invite form can pre-set it rather than asking every time.
      setFirmSettings(tenant?.settings ?? {});
      setAutoReminders(tenant?.autoReminders ?? null);
      // The firm's capital market assumptions become the session's table the moment the firm
      // resolves — every preset, strategy price, Assumptions row and SOA the adviser sees from
      // here runs on them (and rides to the server with each calculation request). Invalid or
      // absent sets leave the AgedCareWise defaults standing.
      setPersistentFirmAssumptions(
        (tenant?.settings?.capitalMarketAssumptions as FirmCapitalMarketAssumptions | null | undefined) ?? null,
      );
      setMessageDefaults(tenant?.messageDefaults ?? null);
      const user = await getSessionUser().catch(() => null);
      // Firms created by a platform admin before the profession was recorded carry null. Read the
      // one fact that decides everything — does it have a licence of its own? — rather than guess.
      const profession: Profession = tenant?.profession ?? (tenant?.afslLicenseeName || tenant?.afslNumber ? 'adviser' : 'other');
      // This person's own ASIC number, which an advice document names alongside the firm's entities.
      // A roster read that fails must not stop the workspace opening — the attribution then reports
      // itself incomplete, which is the truthful outcome.
      const myAdviserNumber =
        (await listFirmMembers(membership.tenantId).catch(() => []))
          .find((m) => m.userId === user?.id)?.adviserNumber ?? null;
      const adviserName =
        [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
        (user?.email ? user.email.split('@')[0] : 'Adviser');
      const roleLabels: Record<string, string> = { owner: 'Firm Owner', adviser: 'Financial Adviser', staff: 'Staff' };
      const roleLabel = roleLabels[membership.role] || 'Member';
      // Load the firm's REAL client list BEFORE revealing the portal. Setting the identity is
      // what lifts the "Opening your workspace…" loader, so it must come LAST — otherwise the
      // portal appears with the demo samples still in state for the moment the client list is
      // still fetching (owner-reported flash of dummy clients). No real data crosses firms:
      // the demo cases are synthetic, and the real list is filtered to this tenant only.
      const profiles: ClientProfileSummary[] = await listClientProfiles().catch(() => []);
      // The id is optional on the wire — the server withholds it from a caller whose one visible file
      // is their OWN (see resolveSoleProfileId). A firm member is staff of the tenant these live in, so
      // it is always present here; narrowing once, at the point the firm's files are picked out, is what
      // lets every use below take it as given rather than each guessing.
      const firmProfiles = profiles.filter((p): p is ClientProfileSummary & { id: string } => p.tenantId === membership.tenantId && p.id !== undefined);
      setProfessionalCases(firmProfiles.map(mapProfileToCase));
      // Pull the ADVICE REGISTER for any file that has one. The portal's SOA list was built purely
      // from sessionStorage, so a client's generated advice disappeared from Reports & SOAs the
      // moment the adviser signed out — even though the register (the legal record) still held every
      // version. Only files the list says have SOAs are fetched, so this costs nothing for the rest.
      void (async () => {
        const withSoas = firmProfiles.filter((p) => (p.workflow?.soaCount ?? 0) > 0);
        const entries = await Promise.all(
          withSoas.map(async (p) => {
            try {
              return [p.id, await listSoas(p.id)] as const;
            } catch {
              // A register that can't be reached must not take the portal down with it; the row still
              // shows its derived SOA status, just without the per-version list.
              return [p.id, []] as const;
            }
          }),
        );
        setRegisteredSoasByProfileId(Object.fromEntries(entries.filter(([, soas]) => soas.length > 0)));
      })();
      // Who advice from this firm comes from. Set here because this is the one place that holds the
      // firm record, the signed-in person and their role together; the document builders read it
      // rather than having it threaded down through every component between.
      setCurrentAdviceAttribution(
        adviceAttribution(
          {
            firmName: tenant?.name || membership.tenantName,
            adviserName,
            adviserNumber: myAdviserNumber,
            licenseeName: tenant?.afslLicenseeName ?? null,
            afslNumber: tenant?.afslNumber ?? null,
            adviceStructure: tenant?.adviceStructure ?? null,
            carName: tenant?.carName ?? null,
            carNumber: tenant?.carNumber ?? null,
          },
          PLATFORM_LICENCE,
        ),
      );
      setPartnerFirm({
        ...membership,
        identity: {
          tenantId: membership.tenantId,
          userId: user?.id || '',
          role: membership.role,
          profession,
          holderLabel: holderLabelFor(profession),
          // "Finished" means something different for the two kinds of firm. An adviser firm has
          // finished when it has said how its advice is provided; a firm that gives no advice has no
          // such question, and is finished once it has set up how it works — reminders, wording,
          // documents — which is what its checklist step points at.
          firmProfileComplete:
            profession === 'adviser'
              ? Boolean(tenant?.adviceStructure)
              : Boolean(
                  tenant?.settings &&
                    (tenant.settings.defaultFollowUpDays !== undefined ||
                      tenant.settings.fsgUrl ||
                      tenant.settings.companyProfileUrl ||
                      tenant.settings.templates),
                ),
          adviserName,
          email: user?.email || '',
          firmName: tenant?.name || membership.tenantName,
          initials: adviserName.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
          roleLine: `${roleLabel} · ${tenant?.name || membership.tenantName}`,
          // Not "operating under our arrangements": a firm that gives no advice is not our
          // representative and must not be described as if it were. We give the advice; they refer.
          licenceLine: tenant?.afslLicenseeName
            ? `${tenant.afslLicenseeName}${tenant.afslNumber ? ` · AFSL ${tenant.afslNumber}` : ''}`
            : 'Advice provided by AgedCareWise under its AFSL · your firm refers clients',
        },
      });
      setPartnerResolving(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, accountUser, portalRefreshNonce]);

  const scopedProfessionalCases = useMemo(() => {
    // REAL firm mode is scoped by who actually created each client file. The role scopes below are
    // demo fixtures — their owner/practice ids ('adv-jm', 'prac-harbour') are invented sample
    // people, so applying them to real data matched nothing and hid every real client from the
    // portal. Firm owners see the whole firm's book; everyone else sees only their own clients.
    if (partnerFirm) {
      if (partnerFirm.role === 'owner') return professionalCases;
      return professionalCases.filter((c) => c.ownerId === accountUser?.id);
    }
    const scope = getRoleScope(professionalRole);
    if (scope.type === 'licensee') return professionalCases;
    if (scope.type === 'practice') return professionalCases.filter((c) => c.practiceId === scope.practiceId);
    return professionalCases.filter((c) => c.ownerId === scope.ownerId);
  }, [professionalCases, professionalRole, partnerFirm, accountUser]);

  const activeProfessionalCase = useMemo(
    () => professionalCases.find((c) => c.id === activeProfessionalCaseId) || null,
    [professionalCases, activeProfessionalCaseId]
  );

  const updateProfessionalCase = (caseId: string, patch: Partial<AppProfessionalCase>) => {
    setProfessionalCases((prev) =>
      prev.map((item) =>
        item.id === caseId
          ? { ...item, ...patch, lastActivityAt: new Date().toISOString() }
          : item
      )
    );
  };

  const markProfessionalCaseSoaStatus = (caseId: string | null | undefined, soaStatus: string) => {
    if (!caseId) return;
    const statusPatch: Partial<AppProfessionalCase> = soaStatus === 'published'
      ? { soaStatus: 'published', status: 'soa_published' }
      : { soaStatus: 'draft', status: 'soa_draft' };
    updateProfessionalCase(caseId, statusPatch);
  };

  const persistCaseStudies = (items: SavedCaseStudy[]) => {
    setSavedCaseStudies(items);
    sessionStorage.setItem('careCompanionCaseStudies', JSON.stringify(items));
    sessionStorage.setItem('careCompanionScenarios', JSON.stringify(items));
  };

  const setCurrentSoaForCase = (caseId: string, soaId: string) => {
    if (!caseId || !soaId) return;
    const next = savedCaseStudies.map((item) => {
      if (item.caseId !== caseId) return item;
      return { ...item, isCurrentForCase: item.id === soaId };
    });
    persistCaseStudies(next);
    updateProfessionalCase(caseId, { currentSoaId: soaId });
  };

  /** Every id the advice register has told us about — lets publishSoaForCase recognise a version
   *  that exists only there, with no local working copy behind it. */
  const registeredSoaIds = useMemo(
    () => new Set(Object.values(registeredSoasByProfileId).flat().map((soa) => soa.id)),
    [registeredSoasByProfileId],
  );

  const publishSoaForCase = (caseId: string, soaId: string) => {
    if (!caseId || !soaId) return;
    const now = new Date().toISOString();
    const next = savedCaseStudies.map((item) => {
      if (item.caseId !== caseId) return item;
      if (item.id === soaId) {
        return { ...item, isCurrentForCase: true, soaDeliveryStatus: 'published', publishedAt: now };
      }
      return { ...item, isCurrentForCase: false, soaDeliveryStatus: 'draft' };
    });
    persistCaseStudies(next);
    updateProfessionalCase(caseId, { currentSoaId: soaId });
    markProfessionalCaseSoaStatus(caseId, 'published');

    // Publication is a compliance event: mirror it to the versioned advice register.
    //
    // `soaId` is either a local working copy's id (which records the register id it was written to)
    // or — for a version that exists ONLY in the register, e.g. one generated before this session —
    // the register id itself. Falling back to it is what lets an adviser publish a draft they can
    // see but have no local snapshot of; before, that draft could be listed but never sent.
    const publishedItem = savedCaseStudies.find((item) => item.id === soaId);
    const serverSoaId = publishedItem?.serverSoaId ? String(publishedItem.serverSoaId) : registeredSoaIds.has(soaId) ? soaId : null;
    if (serverSoaId) {
      publishRegisteredSoa(serverSoaId)
        .then(() => {
          // Reflect the new status in the register copy the portal holds, so the row stops offering
          // "Sent to Client" for something already sent.
          setRegisteredSoasByProfileId((prev) => {
            const next: Record<string, RegisteredSoaSummary[]> = {};
            for (const [pid, list] of Object.entries(prev)) {
              next[pid] = list.map((soa) => (soa.id === serverSoaId ? { ...soa, status: 'published' as const } : soa));
            }
            return next;
          });
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') console.warn('[soa-register publish]', err instanceof Error ? err.message : err);
        });
    }
  };

  const deleteDraftSoa = (soaId: string | number | null | undefined) => {
    if (!soaId) return;
    const target = savedCaseStudies.find((item) => item.id === soaId);
    if (!target) return;
    if ((target.soaDeliveryStatus || 'draft') === 'published') return;
    const caseId = target.caseId || null;
    const remaining = savedCaseStudies.filter((item) => item.id !== soaId);
    const caseSoasAfter = caseId ? remaining.filter((item) => item.caseId === caseId) : [];
    const hasCurrent = caseSoasAfter.some((item) => item.isCurrentForCase);
    const normalizedRemaining = caseId && !hasCurrent && caseSoasAfter.length > 0
      ? remaining.map((item) => {
          if (item.caseId !== caseId) return item;
          const firstForCaseId = caseSoasAfter[0]?.id;
          if (!firstForCaseId) return item;
          return { ...item, isCurrentForCase: item.id === firstForCaseId };
        })
      : remaining;
    persistCaseStudies(normalizedRemaining);
    if (caseId) {
      const caseSoas = normalizedRemaining.filter((item) => item.caseId === caseId);
      const current = caseSoas.find((item) => item.isCurrentForCase) || caseSoas[0] || null;
      if (!current) {
        updateProfessionalCase(caseId, { currentSoaId: undefined, soaStatus: 'none', status: 'adviser_review' });
      } else {
        const currentStatus = (current.soaDeliveryStatus || 'draft') === 'published' ? 'published' : 'draft';
        updateProfessionalCase(caseId, { currentSoaId: String(current.id) });
        markProfessionalCaseSoaStatus(caseId, currentStatus);
      }
    }
  };

  const openProfessionalCaseJourney = (caseId: string) => {
    const target = professionalCases.find((c) => c.id === caseId);
    if (!target) return;
    // Re-arm the once-per-client AWise intro so it greets the user on this client's first
    // insight panel (it then stays dismissed across that client's modules for the session).
    rearmAdviserIntro();

    // REAL client file: fetch + decrypt it from the firm's secure storage, restore the client's
    // strategies/SOAs, and point auto-save at THIS record for the whole working session.
    if (target.isReal) {
      (async () => {
        const loaded = await loadClientProfile(caseId, { remember: false }).catch(() => null);
        if (!loaded) return;
        setActiveProfessionalCaseId(caseId);
        setActiveRealProfileId(caseId);
        setFactFind(loaded.factFind);
        const appData: ProfileAppData = loaded.appData || {};
        setSavedStrategies(Array.isArray(appData.savedStrategies) ? (appData.savedStrategies as SavedStrategy[]) : []);
        setSavedCaseStudies(Array.isArray(appData.savedCaseStudies) ? (appData.savedCaseStudies as SavedCaseStudy[]) : []);
        // This file's setup answers, or none — never the previous file's.
        setCoupleValidation(appData.coupleValidation ? { ...freshCoupleValidation(), ...(appData.coupleValidation as CoupleValidationState) } : freshCoupleValidation());
        clearSessionMirror();
        restoreSessionMirror(appData);
        const appState: ProfileAppState = loaded.appState || {};
        setClientProfileStep(Number(appState.clientProfileStep) || 1);
        const lastPageRaw = appState.lastPage;
        const lastPage = typeof lastPageRaw === 'string' && RESUMABLE_PAGES.includes(lastPageRaw) ? lastPageRaw : 'Dashboard';
        setActivePage(lastPage);
      })();
      return;
    }

    setActiveRealProfileId(null);
    setActiveProfessionalCaseId(caseId);
    setFactFind(structuredClone(target.factFind || INITIAL_FACT_FIND));
    setCoupleValidation(freshCoupleValidation());
    setClientProfileStep(1);
    const statusPatch: Partial<AppProfessionalCase> = target.status === 'draft' || target.status === 'invited'
      ? { status: 'started', profileProgressPct: Math.max(15, target.profileProgressPct || 0), startedAt: target.startedAt || new Date().toISOString() }
      : {};
    if (Object.keys(statusPatch).length) updateProfessionalCase(caseId, statusPatch);

    const resumablePages = ['Dashboard', 'Client Profile', 'AssumptionsPage', 'AgedCarePage', 'HomeCarePage', 'RetirementVillagePage', 'GrannyFlatPage', 'DownsizingPage'];
    const last = target.lastWorkedPage && resumablePages.includes(target.lastWorkedPage) ? target.lastWorkedPage : null;
    if (target.status === 'invited' || target.status === 'draft') {
      setActivePage('Client Profile');
    } else if (last) {
      setActivePage(last);
    } else {
      setActivePage('Dashboard');
    }
  };

  // Before opening a NEW client file in a real firm, confirm the firm has paid capacity for one more
  // client. If not, open the "add another client" paywall and return false. (Billing off → always
  // true; the server gate on POST /v1/profiles is the hard backstop.) Refreshes the stored summary.
  const partnerHasClientCapacity = async (): Promise<boolean> => {
    if (!partnerFirm) return true;
    const res = await getPartnerSummary(partnerFirm.tenantId);
    if (!res.billingConfigured || !res.summary) return true;
    setPartnerBilling(res.summary);
    if (res.summary.canAddClient) return true;
    setAddClientPaywall({ summary: res.summary, tenantId: partnerFirm.tenantId });
    return false;
  };

  // Re-read the firm's client-billing summary after a client is created (a slot was consumed).
  const refreshPartnerBilling = () => {
    if (!partnerFirm) return;
    getPartnerSummary(partnerFirm.tenantId)
      .then((r) => { if (r.summary) setPartnerBilling(r.summary); })
      .catch(() => {});
  };

  const createInPersonProfessionalCase = (inPersonPayload: InPersonCasePayload = {}) => {
    const timestamp = Date.now();
    const first = (inPersonPayload.firstName || '').trim();
    const last = (inPersonPayload.lastName || '').trim();
    const email = (inPersonPayload.email || '').trim();
    const phone = (inPersonPayload.preferredPhone || '').trim();
    const completingAs = inPersonPayload.completingAs || 'self';
    const isClientCompleting = completingAs === 'self';

    const completerMap: Record<string, string> = {
      self: 'client',
      family_member: 'family',
      epoa: 'eop',
    };
    const completer = completerMap[completingAs] || 'client';

    const fd: DraftFactFind = structuredClone(INITIAL_FACT_FIND);
    fd.completer = completer;

    if (isClientCompleting) {
      if (first) fd.client!.firstName = first;
      if (last) fd.client!.lastName = last;
      if (email) fd.client!.email = email;
      if (phone) fd.client!.preferredPhone = phone;
    } else {
      if (first) fd.completerFirstName = first;
      if (last) fd.completerLastName = last;
    }

    const completerDisplay = [first, last].filter(Boolean).join(' ').trim();
    const clientDisplay = [fd.client!.firstName, fd.client!.lastName].filter(Boolean).join(' ').trim();
    const fullName = isClientCompleting
      ? clientDisplay || 'New In-Person Client'
      : clientDisplay || (completerDisplay ? `Applicant TBC (${completerDisplay})` : 'In-person — applicant pending');

    // REAL portal mode: create the client file in the firm's secure storage and open it.
    if (partnerFirm && accountUser) {
      (async () => {
        // Per-client billing gate: only open a new client file if the firm has paid capacity.
        if (!(await partnerHasClientCapacity())) return;
        const saved = await saveClientProfile({ factFind: fd, tenantId: partnerFirm.tenantId }).catch(() => null);
        if (!saved) return;
        setProfessionalCases((prev) => [
          // createdBy mirrors what the server stamped (auth.uid() — this adviser). Without it the
          // freshly created file would be scoped out of its own creator's list until a reload.
          mapProfileToCase({
            // Created in the firm's tenant by a staff member, so the id always comes back.
            id: saved.profileId!,
            displayName: fullName,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: accountUser.id,
          }),
          ...prev,
        ]);
        setActiveProfessionalCaseId(saved.profileId);
        setActiveRealProfileId(saved.profileId);
        setFactFind(fd);
        setSavedStrategies([]);
        setSavedCaseStudies([]);
        setCoupleValidation(freshCoupleValidation());
        clearSessionMirror();
        setClientProfileStep(1);
        setActivePage('Client Profile');
        refreshPartnerBilling();
      })();
      return;
    }

    const newCase: AppProfessionalCase = {
      id: `case-new-${timestamp}`,
      clientName: fullName,
      clientEmail: email || `inperson.${timestamp}@demo.local`,
      ownerId: 'adv-jm',
      ownerName: 'Joncarl La Rosa',
      ownerRole: 'adviser',
      practiceId: 'prac-harbour',
      accessMode: 'in_person',
      inPersonCompletingAs: inPersonPayload.completingAs || 'self',
      status: 'started',
      profileProgressPct: 10,
      startedAt: new Date().toISOString(),
      lastActivityAt: new Date().toISOString(),
      submissionDate: null,
      soaStatus: 'none',
      agedCareRoomCost: 0,
      sahBudget: 0,
      commonStrategy: 'Not set',
      estateStage: 'unknown',
      strategySource: 'manual_control',
      strategyGenerationCount: 0,
      strategyUsageMode: 'manual_only',
      manualStrategiesModelled: 0,
      manualStrategiesSaved: 0,
      suggestionSoaSelection: null,
      homeDecision: 'keep_home',
      assetSellMix: [],
      roomFunding: { mode: 'partial_rad', radPct: 0, dapPct: 0, radAmount: 0, dapAmount: 0 },
      factFind: fd,
      lastWorkedPage: 'Client Profile',
    };
    setProfessionalCases((prev) => [newCase, ...prev]);
    setActiveProfessionalCaseId(newCase.id);
    setFactFind(fd);
    setClientProfileStep(1);
    setActivePage('Client Profile');
  };

  const createInviteProfessionalCase = (invitePayload: InviteCasePayload = {}) => {
    const timestamp = Date.now();
    const seed = inviteFileSeed(invitePayload);
    const first = seed.client.firstName;
    const last = seed.client.lastName;
    const fullName = seed.displayName || 'Draft Invite Client';
    const email = (invitePayload.email || '').trim() || `invite.${timestamp}@demo.local`;
    const mobile = (invitePayload.mobile || '').trim();
    const accessMode: 'client_end_to_end' | 'client_stop_dashboard' =
      invitePayload.accessMode === 'client_end_to_end' ? 'client_end_to_end' : 'client_stop_dashboard';
    const autoFollowUp = invitePayload.autoFollowUp && ['3', '5', '7', 'manual'].includes(invitePayload.autoFollowUp)
      ? invitePayload.autoFollowUp
      : '7';
    const completingAs = invitePayload.completingAs || 'self';

    const fd: DraftFactFind = structuredClone(INITIAL_FACT_FIND);
    if (first) fd.client!.firstName = first;
    if (last) fd.client!.lastName = last;
    if (mobile) fd.client!.preferredPhone = mobile;
    // The adviser's answer to "who will be completing this form?" is step 1's answer on the file,
    // and the names go where the profile keeps them (inviteFileSeed).
    fd.completer = seed.completer;
    if (seed.completerFirstName) fd.completerFirstName = seed.completerFirstName;
    if (seed.completerLastName) fd.completerLastName = seed.completerLastName;
    if (seed.completerRelationship) fd.completerRelationship = seed.completerRelationship;

    // REAL portal mode: create the client's file in the firm's storage AND a one-time invite
    // link (copied to the clipboard) the adviser sends to the client.
    if (partnerFirm && accountUser) {
      (async () => {
        // Per-client billing gate: inviting a client opens a new client file, so it needs capacity.
        if (!(await partnerHasClientCapacity())) return;
        fd.client!.email = email;
        const saved = await saveClientProfile({ factFind: fd, tenantId: partnerFirm.tenantId }).catch(() => null);
        if (!saved) return;
        // 'manual' means the adviser will chase; anything else is a day count the scheduler acts on.
        // Until now this answer was collected by the form and thrown away.
        const followUpDays = autoFollowUp === 'manual' ? null : Number(autoFollowUp) || null;
        const invite = await createClientInvite(partnerFirm.tenantId, email, saved.profileId, followUpDays);
        const link = invite.ok && invite.token ? `${window.location.origin}/?invite=${invite.token}` : null;
        setProfessionalCases((prev) => [
          {
            ...mapProfileToCase({
              // Created in the firm's tenant by a staff member, so the id always comes back.
              id: saved.profileId!,
              displayName: fullName,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              createdBy: accountUser.id,
            }),
            clientEmail: email,
            status: 'invited',
            inviteLink: link,
          },
          ...prev,
        ]);
        refreshPartnerBilling();
        if (link) {
          // Copied either way: even when the server emailed it, an adviser often wants to paste it
          // into their own message, and it is the fallback if the email bounces.
          try { await navigator.clipboard.writeText(link); } catch { /* clipboard may be blocked */ }
          // `emailed` reports whether the server actually DELIVERED it. Claiming "we've sent it"
          // when no email provider is configured would leave the adviser waiting on a client who was
          // never contacted — the exact failure this change exists to remove.
          const emailed = invite.ok && invite.emailed;
          const expires = invite.ok && invite.expiresAt
            ? new Date(invite.expiresAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
            : null;
          notify(
            emailed
              ? {
                    tone: 'success',
                    title: `Invite sent to ${email}`,
                    message: `${fullName || 'Your client'} will receive an email with a secure link to complete their profile.${expires ? ` It expires on ${expires}.` : ''}`,
                    link: { url: link, label: 'Their secure link — already on your clipboard', note: 'Send it yourself if you prefer; it works once.' },
                }
              : {
                    tone: 'warning',
                    title: `Invite created for ${fullName || email} — not emailed`,
                    message: 'Email sending is not switched on for this server, so nothing has reached your client yet. Send them this one-time link yourself.',
                    link: { url: link, label: 'Their secure link — already on your clipboard', note: expires ? `It expires on ${expires}.` : undefined },
                },
          );
        } else {
          const failureMessage = !invite.ok && invite.message ? invite.message : undefined;
          notify({ tone: 'error', title: 'The invite link could not be generated', message: `${failureMessage ? `${failureMessage} ` : ''}The client file was created; you can send the invite again from their row.` });
        }
      })();
      return;
    }

    const newCase: AppProfessionalCase = {
      id: `case-invite-${timestamp}`,
      clientName: fullName,
      clientEmail: email,
      clientMobile: mobile || undefined,
      ownerId: 'adv-jm',
      ownerName: 'Joncarl La Rosa',
      ownerRole: 'adviser',
      practiceId: 'prac-harbour',
      accessMode,
      inviteCompletingAs: completingAs,
      inviteAutoFollowUp: autoFollowUp,
      status: 'invited',
      profileProgressPct: 0,
      startedAt: '', // not started yet — DemoCase.startedAt is non-nullable; falsy '' reads the same as null downstream
      lastActivityAt: new Date().toISOString(),
      submissionDate: null,
      soaStatus: 'none',
      agedCareRoomCost: 0,
      sahBudget: 0,
      commonStrategy: 'Not set',
      estateStage: 'unknown',
      strategySource: 'manual_control',
      strategyGenerationCount: 0,
      strategyUsageMode: 'manual_only',
      manualStrategiesModelled: 0,
      manualStrategiesSaved: 0,
      suggestionSoaSelection: null,
      homeDecision: 'keep_home',
      assetSellMix: [],
      roomFunding: { mode: 'partial_rad', radPct: 0, dapPct: 0, radAmount: 0, dapAmount: 0 },
      factFind: fd,
    };
    setProfessionalCases((prev) => [newCase, ...prev]);
  };

  /**
   * Fetch the fact-finds the Analytics tab needs.
   *
   * The client LIST deliberately carries no fact-find — it is encrypted per client and only decrypted
   * when that client is opened, which is what keeps listing a whole firm's book cheap and private.
   * Analytics, though, is built entirely from fact-find content (ages, goals, assessment stage), so
   * on the client list alone every one of its charts rendered as zero: "0 clients in their 80s" for a
   * firm whose clients are all in their 80s.
   *
   * So it is loaded ON DEMAND, when the adviser actually opens Analytics, rather than up front for a
   * screen they may never visit. One request per client file; each failure is tolerated so a single
   * unreadable file can't blank the whole page. Loaded once per session — the results are patched
   * into the cases already in state.
   */
  const loadFactFindsForAnalytics = async () => {
    if (analyticsDataState === 'loading' || analyticsDataState === 'loaded') return;
    const targets = professionalCases.filter((c) => c.isReal && !c.factFind?.client?.dob);
    if (!targets.length) {
      setAnalyticsDataState('loaded');
      return;
    }
    setAnalyticsDataState('loading');
    let failed = 0;
    await Promise.all(
      targets.map(async (target) => {
        try {
          // remember:false — reading a file for a chart must not change which client the app
          // considers "current", which is what would happen if this stored the profile id.
          const loaded = await loadClientProfile(target.id, { remember: false });
          // Patched directly rather than through updateProfessionalCase: that stamps lastActivityAt,
          // and merely drawing a chart is not activity on the client's file.
          setProfessionalCases((prev) => prev.map((c) => (c.id === target.id ? { ...c, factFind: loaded.factFind } : c)));
        } catch {
          failed += 1;
        }
      }),
    );
    setAnalyticsDataState(failed ? 'partial' : 'loaded');
  };

  /**
   * Chase a client who hasn't opened their invite.
   *
   * Issues a fresh one-time link and emails it (the old one is retired server-side — see
   * resendInvite). Reports back plainly: if email delivery isn't configured the adviser is told the
   * client has NOT been contacted, rather than being left to assume a reminder went out.
   */
  const remindClient = async (caseId: string): Promise<void> => {
    const target = professionalCases.find((c) => c.id === caseId);
    const inviteId = target?.workflow?.inviteId;
    if (!partnerFirm || !inviteId) {
      notify({ tone: 'info', title: 'Nothing to resend', message: 'There is no outstanding invite for this client.' });
      return;
    }
    const result = await resendClientInvite(partnerFirm.tenantId, inviteId);
    if (!result.ok) {
      notify({ tone: 'error', title: 'The reminder could not be sent', message: result.message });
      return;
    }
    const freshLink = result.token ? `${window.location.origin}/?invite=${result.token}` : null;
    const freshExpiry = result.expiresAt ? new Date(result.expiresAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
    if (freshLink) { try { await navigator.clipboard.writeText(freshLink); } catch { /* clipboard may be blocked */ } }
    if (result.emailed) {
      // Reflect the new invite immediately: the reminder resets the clock the "days waiting" and
      // overdue checks read from, so the row shouldn't still look untouched.
      updateProfessionalCase(caseId, {});
      notify({
        tone: 'success',
        title: `New link sent to ${target?.clientName || 'your client'}`,
        message: `A nudge creates a fresh secure link and emails it; the earlier link no longer works.${freshExpiry ? ` This one expires on ${freshExpiry}.` : ''}`,
        ...(freshLink ? { link: { url: freshLink, label: 'The new link — already on your clipboard', note: 'Send it yourself if you prefer; it works once.' } } : {}),
      });
    } else {
      notify({
        tone: 'warning',
        title: `New link created for ${target?.clientName || 'your client'} — not emailed`,
        message: 'Email sending is not switched on for this server, so nothing has reached your client. The earlier link no longer works; send them this one yourself.',
        ...(freshLink ? { link: { url: freshLink, label: 'The new link — already on your clipboard', note: freshExpiry ? `It expires on ${freshExpiry}.` : undefined } } : {}),
      });
    }
  };

  /** Save the firm's working preferences and keep the in-memory copy in step. */
  const saveFirmSettings = async (patch: FirmSettings): Promise<boolean> => {
    if (!partnerFirm) return false;
    const result = await updateFirmSettings(partnerFirm.tenantId, patch);
    if (!result.ok) {
      notify({ tone: 'error', title: 'Those settings could not be saved', message: result.message || undefined });
      return false;
    }
    setFirmSettings((prev) => ({ ...prev, ...patch }));
    return true;
  };

  const submitProfessionalCaseToAdviser = () => {
    if (!activeProfessionalCaseId) return;
    updateProfessionalCase(activeProfessionalCaseId, {
      status: 'submitted',
      profileProgressPct: 100,
      submissionDate: new Date().toISOString(),
      factFind: structuredClone(factFind),
    });
    setActivePage('PartnerPortal');
  };

  useEffect(() => {
    if (!activeProfessionalCaseId) return;
    const labels = deriveEngagementLabelsFromFactFind(factFind);
    updateProfessionalCase(activeProfessionalCaseId, {
      factFind: structuredClone(factFind),
      ...labels,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factFind, activeProfessionalCaseId]);

  useEffect(() => {
    if (!activeProfessionalCaseId) return;
    if (activePage === 'Client Profile') {
      updateProfessionalCase(activeProfessionalCaseId, {
        status: 'started',
        profileProgressPct: Math.max(15, Math.min(95, Math.round((clientProfileStep / 8) * 100))),
      });
    } else if (activePage === 'Dashboard') {
      const curr = professionalCases.find((c) => c.id === activeProfessionalCaseId);
      if (curr && curr.accessMode === 'client_stop_dashboard' && !curr.submissionDate) {
        updateProfessionalCase(activeProfessionalCaseId, {
          status: 'submitted',
          profileProgressPct: 100,
          submissionDate: new Date().toISOString(),
        });
      }
    } else if (['AgedCarePage', 'HomeCarePage', 'RetirementVillagePage'].includes(activePage)) {
      const curr = professionalCases.find((c) => c.id === activeProfessionalCaseId);
      updateProfessionalCase(activeProfessionalCaseId, {
        status: 'adviser_review',
        ...(curr && !curr.submissionDate ? { submissionDate: new Date().toISOString() } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, clientProfileStep, activeProfessionalCaseId]);

  useEffect(() => {
    if (!activeProfessionalCaseId) return;
    if (activePage === 'LandingPage' || activePage === 'PartnerPortal') return;
    updateProfessionalCase(activeProfessionalCaseId, { lastWorkedPage: activePage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, activeProfessionalCaseId]);

  return {
    professionalRole,
    setProfessionalRole,
    setProfessionalCases,
    scopedProfessionalCases,
    activeProfessionalCaseId,
    setActiveProfessionalCaseId,
    activeProfessionalCase,
    partnerPortalReviewNonce,
    setPartnerPortalReviewNonce,
    partnerIntent,
    setPartnerIntent,
    partnerFirm,
    setPartnerFirm,
    activeRealProfileId,
    setActiveRealProfileId,
    partnerResolving,
    setPartnerResolving,
    portalEntryError,
    setPortalEntryError,
    setPortalRefreshNonce,
    partnerPaywall,
    setPartnerPaywall,
    partnerBilling,
    addClientPaywall,
    setAddClientPaywall,
    showPartnerWizard,
    setShowPartnerWizard,
    updateProfessionalCase,
    markProfessionalCaseSoaStatus,
    setCurrentSoaForCase,
    publishSoaForCase,
    deleteDraftSoa,
    openProfessionalCaseJourney,
    createInPersonProfessionalCase,
    createInviteProfessionalCase,
    submitProfessionalCaseToAdviser,
    registeredSoasByProfileId,
    analyticsDataState,
    loadFactFindsForAnalytics,
    remindClient,
    firmSettings,
    autoReminders,
    messageDefaults,
    saveFirmSettings,
  };
}
